import React, { useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { styled } from 'twin.macro';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { StaticDatePicker } from '@mui/x-date-pickers';

import Icon from '@sopranodesign/pulse-icons';

import { getPickerProps, DateInput } from './Date';
import { DateProvider } from '../../../../../Providers';
import { DateWrapper } from '../../../../../Components';
import {
  useComposer,
  useDelivery,
  useRTL
} from '../../../../../hooks';

const isValid = date => dayjs(date, 'DD-MM-YYYY').isValid();

const CustomCalendarHeaderRoot = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '8px 16px',
  alignItems: 'center',
});

function CustomCalendarHeader({ date, onMonthChange, onYearClick }) {
  const selectNextMonth = () => onMonthChange(dayjs(date).add(1, 'month'));
  const selectPreviousMonth = () => onMonthChange(dayjs(date).subtract(1, 'month'));

  return (
    <CustomCalendarHeaderRoot>
      <Stack spacing={1} direction="row">
        <Icon
          type="cheveronLeft"
          width="1.25rem"
          height="1.25rem"
          onClick={selectPreviousMonth}
          title="Previous month"
        />
      </Stack>
      <Typography
        variant="body2"
        style={{ cursor: 'pointer' }}
        onClick={onYearClick}
      >
        {dayjs(date).format('MMMM YYYY')}
      </Typography>
      <Stack spacing={1} direction="row">
        <Icon
          type="cheveronRight"
          width="1.25rem"
          height="1.25rem"
          onClick={selectNextMonth}
          title="Next month"
        />
      </Stack>
    </CustomCalendarHeaderRoot>
  );
}

CustomCalendarHeader.propTypes = {
  date: PropTypes.instanceOf(dayjs).isRequired,
  onMonthChange: PropTypes.func.isRequired,
  onYearClick: PropTypes.func.isRequired,
};

const DateComposer = props => {
  const [open, setOpen] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const { t } = useTranslation('bots');
  const { dir } = useRTL();
  const { reply, setReply } = useComposer();
  const { sendMessage } = useDelivery();

  const handleChange = date => {
    setReply(dayjs(date).format('DD-MM-YYYY'));

    if (showYearPicker) {
      setShowYearPicker(false);
    }
  };

  const handleInputClick = () => {
    if (!initialized && !reply) {
      setReply(dayjs().format('DD-MM-YYYY'));
      setInitialized(true);
    }

    setOpen(prev => !prev);
  };

  const handleSend = () => {
    if (isValid(reply)) {
      sendMessage(reply);
      setOpen(false);
      setReply('');
    }
  };

  const value = reply ? dayjs(reply, 'DD-MM-YYYY') : dayjs();

  return (
    <DateProvider
      dir={dir}
    >
      <DateWrapper>
        {open && (
          <StaticDatePicker
            localeText={{
              datePickerToolbarTitle: dayjs(value).format('YYYY'),
            }}
            margin="dense"
            maxDate={dayjs().add(10, 'year')}
            onAccept={date => {
              handleChange(date);
              setOpen(false);
            }}
            onChange={handleChange}
            value={value}
            views={['year', 'month', 'day']}
            openTo={showYearPicker ? 'year' : 'day'}
            slots={{
              calendarHeader: CustomCalendarHeader,
            }}
            slotProps={{
              calendarHeader: {
                date: value,
                onYearClick: () => setShowYearPicker(prev => !prev),
                onMonthChange: newDate => handleChange(newDate),
              },
              actionBar: { actions: [] },
              toolbar: {
                onClick: () => setShowYearPicker(prev => !prev),
                sx: { cursor: 'pointer' },
              },
            }}
            {...getPickerProps(props.variant, { open, setOpen, handleChange })}
          />
        )}
        <DateInput
          placeholder={t('date_composer_label')}
          aria-label={t('date_composer_label')}
          value={reply}
          onClick={handleInputClick}
          handleSubmit={handleSend}
        />
      </DateWrapper>
    </DateProvider>
  );
};

DateComposer.propTypes = {
  variant: PropTypes.oneOf(['dialog', 'static']),
};

export default DateComposer;